import React, { createContext, useContext } from 'react';
import { useKeycloak } from '../auth/KeycloakProvider';

const AuthContext = createContext(null);

// Fallback: decode JWT payload when idTokenParsed is not available (e.g. keycloak-js v26)
function parseIdTokenPayload(idToken) {
  if (!idToken || typeof idToken !== 'string') return null;
  try {
    const parts = idToken.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const { keycloak, authenticated, initialized } = useKeycloak();

  const getUser = () => {
    // Prefer idTokenParsed; fallback to tokenParsed; if neither has profile claims, decode idToken
    let parsed = keycloak?.idTokenParsed || keycloak?.tokenParsed;
    if (!parsed && keycloak?.idToken) {
      parsed = parseIdTokenPayload(keycloak.idToken);
    }
    if (authenticated && parsed) {
      return {
        name: parsed.name || parsed.preferred_username || 'User',
        email: parsed.email || '',
        username: parsed.preferred_username || 'user',
      };
    }
    return null;
  };

  const login = () => {
    if (keycloak) {
      keycloak.login({ redirectUri: window.location.origin + '/' });
    }
  };

  const logout = () => {
    if (keycloak) {
      keycloak.logout({ redirectUri: window.location.origin + '/' });
    }
  };

  const value = {
    user: getUser(),
    login,
    logout,
    isAuthenticated: authenticated,
    initialized: initialized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
